import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';

interface DocumentPreviewerProps {
  fileUrl: string;
  fileName: string;
}

const DocumentPreviewer: React.FC<DocumentPreviewerProps> = ({ fileUrl, fileName }) => {
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAndProcessFile = async () => {
      setIsLoading(true);
      setError(null);
      setPreviewContent(null);

      // With a public bucket, we don't need to add any authentication tokens.
      // The fileUrl is the direct public URL to the S3 object.
      const publicUrl = fileUrl;

      try {
        const fileExtension = fileName.split('.').pop()?.toLowerCase();

        if (['pdf'].includes(fileExtension || '')) {
          setPreviewContent(`<iframe src="${publicUrl}" width="100%" height="100%" style="border: none;"></iframe>`);
        } else if (['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(fileExtension || '')) {
          setPreviewContent(`<img src="${publicUrl}" alt="${fileName}" style="max-width: 100%; max-height: 100%; object-fit: contain;" />`);
        } else {
          const response = await fetch(publicUrl);
          if (!response.ok) {
            throw new Error('Error al descargar el archivo para la vista previa.');
          }
          const blob = await response.blob();

          if (fileExtension === 'docx') {
            const arrayBuffer = await blob.arrayBuffer();
            const result = await mammoth.convertToHtml({ arrayBuffer });
            setPreviewContent(result.value);
          } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
            const arrayBuffer = await blob.arrayBuffer();
            const data = new Uint8Array(arrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const html = XLSX.utils.sheet_to_html(worksheet);
            setPreviewContent(html);
          } else if (fileExtension === 'html' || fileExtension === 'htm') {
            setPreviewContent(await blob.text());
          } else {
            setError('No hay vista previa disponible para este tipo de archivo.');
          }
        }
      } catch (err: any) {
        setError(err.message || 'Ocurrió un error al procesar el archivo.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    if (fileUrl && fileName) {
      fetchAndProcessFile();
    }
  }, [fileUrl, fileName]);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (previewContent) {
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    if (['pdf', 'png', 'jpg', 'jpeg', 'gif', 'svg', 'html', 'htm'].includes(fileExtension || '')) {
        return <Box dangerouslySetInnerHTML={{ __html: previewContent }} sx={{ width: '100%', height: '100%' }} />;
    }
    return <Box dangerouslySetInnerHTML={{ __html: previewContent }} sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2, backgroundColor: '#fff', border: '1px solid #ddd' }} />;
  }

  return <Typography>Seleccione un archivo para ver la vista previa.</Typography>;
};

export default DocumentPreviewer;